"use client";

import { useMemo } from "react";
import { StaticImageData } from "next/legacy/image";

import Multimedia, { MultimediaProps } from "@stadium/ui/src/components/multimedia/multimedia";

import { replaceCloudinaryDimensions } from "@helpers/menu";

import { isCloudinaryUrl } from "@stadium/helpers/cloudinary";
import { MAX_WIDTH, mobileScreenType, tabletScreenType } from "@stadium/helpers/style";
import { getOptimizedImageUrl, isUnoptimizedImageUrl } from "@stadium/helpers/multimedia";

type MultimediaWithFlagProps = MultimediaProps & {
  /**
   * Mobile image dimensions (optional, defaults to the image dimensions of the src)
   */
  mobile?: {
    /**
     * Mobile image width in pixels (optional, defaults to 'auto')
     */
    width?: number;
    /**
     * Mobile image height in pixels (Mandatory)
     */
    height: number;
  };
  /**
   * Tablet image dimensions
   */
  tablet?: {
    /**
     * Tablet image width in pixels (optional, defaults to 'auto')
     */
    width?: number;
    /**
     * Tablet image height in pixels (Mandatory)
     */
    height: number;
  };
};

const generateSrcSetAttributes = ({
  mobileSrc,
  tabletSrc,
}: {
  mobileSrc: string;
  tabletSrc: string;
}) => {
  const maxMobileWidth = MAX_WIDTH[mobileScreenType].replace("px", "");
  const maxTabletWidth = MAX_WIDTH[tabletScreenType].replace("px", "");
  const srcSet: string[] = [];
  const sizes: string[] = [];
  // Add mobile first (more specific breakpoint) so it takes precedence
  if (mobileSrc) {
    srcSet.push(`${mobileSrc} ${maxMobileWidth}w`);
    sizes.push(`(max-width: ${maxMobileWidth}px) ${maxMobileWidth}px`);
  }
  if (tabletSrc) {
    srcSet.push(`${tabletSrc} ${maxTabletWidth}w`);
    sizes.push(`(max-width: ${maxTabletWidth}px) ${maxTabletWidth}px`);
  }
  if (sizes.length) {
    return { srcSet: srcSet.join(", "), sizes: sizes.join(", ") };
  }
  return {};
};

const MultimediaWithFlag: React.FC<MultimediaWithFlagProps> = (props) => {
  const { mobile, tablet, src, type, ...restProps } = props;

  // Extract string URL from potential StaticImageData object
  const imageSrc = typeof src === "object" ? (src as StaticImageData)?.src : src;

  const { isCloudinaryImage, isUnoptimizedImage } = useMemo(
    () => ({
      isCloudinaryImage: isCloudinaryUrl(imageSrc),
      isUnoptimizedImage: isUnoptimizedImageUrl(imageSrc),
    }),
    [imageSrc],
  );

  const srcAttributes = useMemo(() => {
    if (type !== "image" || (!isCloudinaryImage && !isUnoptimizedImage)) {
      return {
        src,
      };
    }

    const getOptimizedSrc = (height: number, width: number) =>
      isUnoptimizedImage
        ? getOptimizedImageUrl(src, width, width ? undefined : height)
        : replaceCloudinaryDimensions({
            url: imageSrc,
            height,
            width,
          });

    const mobileSrc = mobile?.height ? getOptimizedSrc(mobile?.height, mobile?.width!) : "";
    const tabletSrc = tablet?.height ? getOptimizedSrc(tablet?.height, tablet?.width!) : "";

    // Extract string URLs from potential StaticImageData objects for srcSet
    const mobileSrcString = typeof mobileSrc === "object" ? mobileSrc.src : mobileSrc;
    const tabletSrcString = typeof tabletSrc === "object" ? tabletSrc.src : tabletSrc;

    return {
      src: getOptimizedSrc(props.height!, props.width!),
      ...generateSrcSetAttributes({
        mobileSrc: mobileSrcString,
        tabletSrc: tabletSrcString,
      }),
    };
  }, [
    props.height,
    props.width,
    mobile?.height,
    mobile?.width,
    tablet?.height,
    tablet?.width,
    type,
    src,
    imageSrc,
    isCloudinaryImage,
    isUnoptimizedImage,
  ]);

  return (
    <Multimedia
      {...restProps}
      type={type}
      {...srcAttributes}
      rewriteToOptimizedCdnUrl={false}
      isNextImage={false}
    />
  );
};

export default MultimediaWithFlag;
